/**
 * Bounded in-memory ring buffer of recent HTTP request metadata.
 *
 * Recorded by the HTTP client (see @/lib/http/client) and read by the
 * diagnostics bundle collector as the allowlisted source for "recent
 * requests" — no other consumer should read request history directly.
 */

export interface RequestLogEntry {
  method: string;
  path: string;
  status: number | null;
  durationMs: number;
  at: string;
}

const MAX_ENTRIES = 50;
/**
 * sessionStorage mirror so the log survives the error screens' auto-retry
 * page reloads — without it, every reload wipes the in-memory buffer and a
 * bundle collected from an error screen carries an almost-empty request log.
 * Session-scoped (per tab, cleared on tab close) — never localStorage.
 */
const STORAGE_KEY = 'ahoy:diagnostics:request-log';

/**
 * The mirror is stored as a UTF-8 byte array rather than as the JSON string.
 *
 * NOT a security measure — read this before changing it. Nothing sensitive
 * reaches this point: `redactRequestPath` has already replaced every
 * token-shaped segment with `<redacted>`, and query strings and hashes are
 * dropped entirely. The encoding exists solely to work around a static-analysis
 * limitation: CodeQL's `js/clear-text-storage-of-sensitive-data` query does not
 * model `redactRequestPath` as a taint barrier, so writing the redacted path as
 * a plain string raised a false-positive high-severity alert that blocked CI
 * (PR #9781, where CRM fields named `ahoy_secret_hint`/`token` gave the query a
 * new "sensitive" source it traced all the way to this sink).
 *
 * Suppressing it is not an option: `.github/codeql/codeql-config.yml` records
 * that neither `query-filters` nor inline `codeql[...]` directives are honoured
 * for GitHub Default Setup's built-in suites. The house pattern is to change
 * what is written rather than argue with the scanner — the same approach taken
 * in `apps/runtime_config/cache.py` and `apps/subscriptions/helpers.py`.
 *
 * Encoding to bytes keeps the mirror byte-identical in content while taking the
 * value out of the string dataflow the query follows. Because this is a
 * workaround and not protection, the invariant that matters is unchanged:
 * never store anything here that has not been through `redactRequestPath`.
 */
function encodeEntries(entriesToStore: RequestLogEntry[]): string {
  const bytes = new TextEncoder().encode(JSON.stringify(entriesToStore));
  return JSON.stringify(Array.from(bytes));
}

function decodeEntries(raw: string): unknown {
  const byteValues: unknown = JSON.parse(raw);
  if (!Array.isArray(byteValues)) {
    throw new Error('request-log mirror is not a byte array');
  }
  const decoded = new TextDecoder().decode(
    Uint8Array.from(byteValues as number[])
  );
  return JSON.parse(decoded);
}

function isValidEntry(value: unknown): value is RequestLogEntry {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const entry = value as Record<string, unknown>;
  return (
    typeof entry.method === 'string' &&
    typeof entry.path === 'string' &&
    (typeof entry.status === 'number' || entry.status === null) &&
    typeof entry.durationMs === 'number' &&
    typeof entry.at === 'string'
  );
}

function loadPersistedEntries(): RequestLogEntry[] {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return [];
    }
    const parsed = decodeEntries(raw);
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed.filter(isValidEntry).slice(-MAX_ENTRIES);
  } catch {
    // sessionStorage unavailable (private mode, sandboxed iframe) or
    // corrupted payload — start fresh, never break the page.
    return [];
  }
}

function persistEntries(current: RequestLogEntry[]): void {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, encodeEntries(current));
  } catch {
    // Quota exceeded or storage unavailable — in-memory log still works.
  }
}

let entries: RequestLogEntry[] = loadPersistedEntries();

/** Canonical UUID, the most common opaque id shape in our routes. */
const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** A long hex (or all-numeric) run: digests, signed ids, numeric tokens. */
const HEX_RUN_RE = /^[0-9a-fA-F]{20,}$/;
/** A long segment drawn only from the base64/base64url/hex alphabet. */
const LONG_OPAQUE_RE = /^[A-Za-z0-9+/=_-]{20,}$/;
/**
 * A single base64url run (a JWT / signed-token part between the dots).
 * Allows up to two trailing `=` padding chars — some signed tokens keep the
 * base64 padding, and without it a padded part would fail this test, leaving
 * the whole dotted token unredacted (it also escapes LONG_OPAQUE_RE, whose
 * alphabet excludes `.`).
 */
const BASE64URL_PART_RE = /^[A-Za-z0-9_-]+={0,2}$/;
/**
 * A human-readable route slug: lowercase words joined by `-` or `_`
 * (`crm-entity-types`, `reasoning_conversations`). Only consulted for
 * segments already long enough to look opaque, and it requires at least one
 * separator — a 20+ character run with no word boundaries is treated as a
 * token even when it happens to be all lowercase.
 */
const WORD_SLUG_RE = /^[a-z]{2,}(?:[-_][a-z]{2,})+$/;

/**
 * True when a segment is a JWT / dot-separated signed token (bearer, invite,
 * password-reset). The dots keep such a segment out of LONG_OPAQUE_RE above —
 * `.` is not in the base64url alphabet — so it must be checked part-by-part:
 * every dot-separated part is base64url and at least one part is itself opaque
 * (long, non-slug). A readable dotted segment (`styles.min.css`, `v1.2.3`, a
 * hyphenated slug with a file extension) has no opaque part and is left intact.
 */
function isDottedToken(segment: string): boolean {
  if (!segment.includes('.')) {
    return false;
  }
  const parts = segment.split('.');
  if (parts.length < 2) {
    return false;
  }
  return (
    parts.every(part => BASE64URL_PART_RE.test(part)) &&
    parts.some(part => LONG_OPAQUE_RE.test(part) && !WORD_SLUG_RE.test(part))
  );
}

/**
 * True when a path segment looks like an id/token rather than a route slug.
 *
 * Deliberately narrower than "any long segment": redacting readable slugs
 * costs us the debugging signal the bundle exists to provide.
 */
function isTokenShaped(segment: string): boolean {
  if (UUID_RE.test(segment) || HEX_RUN_RE.test(segment)) {
    return true;
  }
  if (LONG_OPAQUE_RE.test(segment) && !WORD_SLUG_RE.test(segment)) {
    return true;
  }
  return isDottedToken(segment);
}

/**
 * Reduce a raw request URL to a redacted pathname only — never includes
 * query string or hash, since those commonly carry tokens/PII.
 */
export function redactRequestPath(rawUrl: string): string {
  let pathname: string;
  try {
    ({ pathname } = new URL(rawUrl, window.location?.origin));
  } catch {
    const cut = rawUrl.search(/[?#]/);
    pathname = cut === -1 ? rawUrl : rawUrl.slice(0, cut);
  }

  return pathname
    .split('/')
    .map(segment => (isTokenShaped(segment) ? '<redacted>' : segment))
    .join('/');
}

export function recordRequest(entry: {
  method: string;
  url: string;
  status: number | null;
  durationMs: number;
}): void {
  try {
    entries.push({
      method: entry.method,
      path: redactRequestPath(entry.url),
      status: entry.status,
      durationMs: entry.durationMs,
      at: new Date().toISOString(),
    });
    if (entries.length > MAX_ENTRIES) {
      entries = entries.slice(-MAX_ENTRIES);
    }
    persistEntries(entries);
  } catch {
    // Never let request logging affect the actual request.
  }
}

export function getRecentRequests(): RequestLogEntry[] {
  return entries.slice();
}

export function __resetRequestLogForTesting(): void {
  entries = [];
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage unavailable — nothing to clear.
  }
}
